/**
 * 🌐 SmartBoutik - Contexte Offline
 * Fournit l'état de connexion et les fonctionnalités offline à toute l'application
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import syncService, { SYNC_STATUS } from '../services/syncService';

// Création du contexte
const OfflineContext = createContext();

/**
 * Hook pour utiliser le contexte offline
 */
export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline doit être utilisé dans un OfflineProvider');
  }
  return context;
};

/**
 * Provider du contexte offline
 */
export const OfflineProvider = ({ children }) => {
  // États
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState(SYNC_STATUS.IDLE);
  const [lastSync, setLastSync] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncStats, setSyncStats] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  
  // Ref pour éviter les initialisations multiples
  const initRef = useRef(false);

  /**
   * ✅ Écouter les événements online/offline du navigateur (léger)
   */
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Connexion rétablie');
      setIsOnline(true);
    };
    
    const handleOffline = () => {
      console.log('📴 Connexion perdue');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  /**
   * Initialiser le service de synchronisation
   */
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const initializeOffline = async () => {
      try {
        console.log('🚀 Initialisation du contexte offline...');
        
        const state = await syncService.initSyncService();
        
        setIsOnline(state.isOnline);
        setSyncStatus(state.status);
        setLastSync(state.lastSync);
        setPendingCount(state.pendingCount);
        setIsInitialized(true);
        
        console.log('✅ Contexte offline initialisé');
      } catch (err) {
        console.error('❌ Erreur initialisation offline:', err);
        setError(err.message);
        setIsInitialized(true);
      }
    };

    initializeOffline();

    return () => {
      syncService.cleanupSyncService();
    };
  }, []);

  /**
   * Écouter les changements d'état de synchronisation
   */
  useEffect(() => {
    const unsubscribe = syncService.addSyncListener((state) => {
      setIsOnline(state.isOnline);
      setSyncStatus(state.status);
      setLastSync(state.lastSync);
      setPendingCount(state.pendingCount);
    });

    return unsubscribe;
  }, []);

  /**
   * Rafraîchir les statistiques
   */
  const refreshStats = useCallback(async () => {
    try {
      const stats = await syncService.getSyncStats();
      setSyncStats(stats);
      return stats;
    } catch (err) {
      console.error('Erreur récupération stats:', err);
      return null;
    }
  }, []);

  /**
   * Forcer une synchronisation
   */
  const forceSync = useCallback(async () => {
    setError(null);
    try {
      const result = await syncService.forceSync();
      await refreshStats();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [refreshStats]);

  /**
   * Créer une vente (online ou offline)
   */
  const createSale = useCallback(async (saleData) => {
    try {
      const result = await syncService.createSale(saleData);
      await refreshStats();
      return result;
    } catch (err) {
      console.error('Erreur création vente:', err);
      throw err;
    }
  }, [refreshStats]);

  /**
   * Obtenir les produits (avec cache)
   */
  const getProducts = useCallback(async () => {
    return syncService.getProducts();
  }, []);

  /**
   * Rechercher produit par code-barres
   */
  const getProductByBarcode = useCallback(async (barcode) => {
    return syncService.getProductByBarcode(barcode);
  }, []);

  /**
   * Obtenir les clients (avec cache)
   */
  const getCustomers = useCallback(async () => {
    return syncService.getCustomers();
  }, []);

  /**
   * Obtenir les catégories (avec cache)
   */
  const getCategories = useCallback(async () => {
    return syncService.getCategories();
  }, []);

  /**
   * Réinitialiser les données offline
   */
  const resetOfflineData = useCallback(async () => {
    await syncService.resetOfflineData();
    await refreshStats();
  }, [refreshStats]);

  // Valeur du contexte
  const value = {
    // État de connexion
    isOnline,
    isOffline: !isOnline,
    
    // État de synchronisation
    syncStatus,
    isSyncing: syncStatus === SYNC_STATUS.SYNCING,
    syncError: syncStatus === SYNC_STATUS.ERROR,
    
    // Métadonnées
    lastSync,
    pendingCount,
    hasPendingChanges: pendingCount > 0,
    syncStats,
    
    // État général
    isInitialized,
    error,
    
    // Actions
    forceSync,
    refreshStats,
    resetOfflineData,
    
    // Helpers pour les données
    createSale,
    getProducts,
    getProductByBarcode,
    getCustomers,
    getCategories
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineContext;